/**
 * EditItemSheet — edit a placed planner item (spec §8.3, §5.2 refinements).
 *
 * Editable: activity, date, time, duration and note. The note saves on blur
 * AND on Enter (roadmap refinement; PlannerItem.notes previously existed in
 * the type but was never editable). Move and resize are fully accessible
 * without dragging: 15-minute move buttons plus a duration stepper.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  BottomSheet,
  Button,
  DateField,
  Select,
  Stepper,
  SyncStatus,
  TextField,
  TimeField,
} from '@/components/ui'
import { ACTIVITIES, ACTIVITY_CATEGORIES, getActivityById } from '@/data/activities'
import type { PlannerItem, SyncSaveState } from '@/types'
import { QUARTER_MINUTES } from './collisions'
import { formatMinutes, parseTimeToMinutes } from './plannerDates'

export type EditValues = {
  activityId: string
  date: string
  startMinute: number
  durationMinutes: number
  notes: string
}

export type EditSaveResult =
  | { ok: true }
  | { ok: false; message: string; suggestedStartMinute?: number }

export type EditItemSheetProps = {
  open: boolean
  item: PlannerItem
  syncState: SyncSaveState | null
  saving: boolean
  onClose: () => void
  onSave: (values: EditValues) => Promise<EditSaveResult>
  /** Persists the note immediately (blur/Enter), outside the full save. */
  onSaveNote: (notes: string) => Promise<void>
}

const MIN_DURATION = 10
const MAX_DURATION = 10 * 60

export function EditItemSheet({
  open,
  item,
  syncState,
  saving,
  onClose,
  onSave,
  onSaveNote,
}: EditItemSheetProps) {
  const [activityId, setActivityId] = useState(item.activityId)
  const [date, setDate] = useState(item.date)
  const [time, setTime] = useState(formatMinutes(item.startMinute))
  const [duration, setDuration] = useState(item.durationMinutes)
  const [notes, setNotes] = useState(item.notes ?? '')
  const [formError, setFormError] = useState<string | null>(null)
  const [suggestion, setSuggestion] = useState<number | null>(null)
  const [noteStatus, setNoteStatus] = useState<'idle' | 'saved'>('idle')

  // Seed the form once per opened item. Later updates of the same item
  // (sync-state changes, note-save round trips) must NOT wipe unsaved edits.
  const seededForIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (seededForIdRef.current === item.id) return
    seededForIdRef.current = item.id
    setActivityId(item.activityId)
    setDate(item.date)
    setTime(formatMinutes(item.startMinute))
    setDuration(item.durationMinutes)
    setNotes(item.notes ?? '')
    setFormError(null)
    setSuggestion(null)
    setNoteStatus('idle')
  }, [item])

  const activityOptions = useMemo(() => {
    const categoryName = new Map(ACTIVITY_CATEGORIES.map((category) => [category.id, category.name]))
    return ACTIVITIES.map((activity) => ({
      value: activity.id,
      label: `${activity.name} — ${categoryName.get(activity.categoryId) ?? ''}`,
    }))
  }, [])

  const startMinute = parseTimeToMinutes(time)
  const timeValid = startMinute !== null && startMinute % QUARTER_MINUTES === 0

  const shiftTime = (deltaMinutes: number) => {
    const base = startMinute ?? 0
    const next = Math.min(Math.max(base + deltaMinutes, 0), 24 * 60 - QUARTER_MINUTES)
    setTime(formatMinutes(next))
    setSuggestion(null)
  }

  const saveNoteIfChanged = async () => {
    const trimmed = notes.trim()
    if (trimmed === (item.notes ?? '')) return
    await onSaveNote(trimmed)
    setNoteStatus('saved')
  }

  const handleSave = async () => {
    setFormError(null)
    setSuggestion(null)
    if (startMinute === null) {
      setFormError('Vul een geldige tijd in, bijvoorbeeld 14:15.')
      return
    }
    if (!timeValid) {
      setFormError('Kies een tijd op een kwartier, bijvoorbeeld 14:00 of 14:15.')
      return
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setFormError('Vul een geldige datum in.')
      return
    }
    const result = await onSave({
      activityId,
      date,
      startMinute,
      durationMinutes: duration,
      notes: notes.trim(),
    })
    if (!result.ok) {
      setFormError(result.message)
      if (result.suggestedStartMinute !== undefined) setSuggestion(result.suggestedStartMinute)
    }
  }

  return (
    <BottomSheet open={open} onClose={onClose} title="Activiteit bewerken">
      <div className="planner-edit">
        <div className="planner-edit__sync">
          <SyncStatus state={syncState} />
        </div>

        <Select
          label="Activiteit"
          value={getActivityById(activityId)?.id ?? activityId}
          onChange={setActivityId}
          options={activityOptions}
        />

        <DateField label="Datum" value={date} onChange={setDate} />

        <TimeField
          label="Begintijd"
          value={time}
          onChange={(value) => {
            setTime(value)
            setSuggestion(null)
          }}
        />

        <div className="planner-edit__move" role="group" aria-label="Verplaats in tijd">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => shiftTime(-QUARTER_MINUTES)}
            disabled={saving || (startMinute ?? 0) <= 0}
          >
            15 min eerder
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => shiftTime(QUARTER_MINUTES)}
            disabled={saving || (startMinute ?? 0) >= 24 * 60 - QUARTER_MINUTES}
          >
            15 min later
          </Button>
        </div>

        <Stepper
          label="Duur (minuten)"
          value={duration}
          onChange={setDuration}
          min={MIN_DURATION}
          max={MAX_DURATION}
          step={5}
          unit="min"
        />

        <div
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              void saveNoteIfChanged()
            }
          }}
        >
          <TextField
            label="Notitie"
            value={notes}
            onChange={(value) => {
              setNotes(value)
              setNoteStatus('idle')
            }}
            onBlur={() => void saveNoteIfChanged()}
            placeholder="Bijvoorbeeld: spullen meenemen"
            hint="Wordt opgeslagen zodra je het veld verlaat of op Enter drukt."
          />
        </div>
        <p className="planner-edit__note-status" role="status" aria-live="polite">
          {noteStatus === 'saved' ? 'Notitie opgeslagen.' : ''}
        </p>

        {formError ? (
          <p className="planner-edit__error" role="alert">
            {formError}
          </p>
        ) : null}
        {suggestion !== null ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setTime(formatMinutes(suggestion))
              setSuggestion(null)
            }}
          >
            Gebruik volgende vrije plek: {formatMinutes(suggestion)}
          </Button>
        ) : null}

        <div className="planner-edit__actions">
          <Button variant="primary" fullWidth loading={saving} disabled={saving} onClick={() => void handleSave()}>
            Opslaan
          </Button>
          <Button variant="ghost" fullWidth onClick={onClose} disabled={saving}>
            Annuleren
          </Button>
        </div>
      </div>
    </BottomSheet>
  )
}
